import { supabase } from "./supabase";
import {
  REGISTRATION_CHANNELS,
  type OpenRegistrationCap,
  type RegistrationChannel,
} from "../utils/openRegistration";

export interface EventExecutiveSummaryRow {
  event_id: string;
  event_title: string;
  event_type: string;
  event_status: string;
  event_start_at: string | null;
  event_end_at: string | null;
  event_timezone: string;
  event_local_date: string | null;
  location_display: string;
  event_state: string | null;
  /** UNIQUE PEOPLE. Never a message count. */
  invited_people_count: number;
  /** Messages from initial invitation flights (eib_flight_role='invite'). */
  campaign_invites_sent_count: number;
  /** Messages from reminder flights (eib_flight_role='invite_reminder'). */
  reminder_messages_count: number;
  /** Every message on the event's campaigns, whatever the flight role. */
  total_campaign_messages_count: number;
  member_registrations: number;
  guest_registrations: number;
  member_self_checkins: number;
  nonmember_self_checkins: number;
  member_operator_checkins: number;
  nonmember_operator_checkins: number;
  cta_member_checkins: number;
  cta_nonmember_checkins: number;
  unclassified_member_checkins: number;
  unclassified_nonmember_checkins: number;
  /**
   * New AND returning members (Zuora accounts, once per event) whose subscription was created with the event
   * organization's promo code between opening time and midnight of the last day. Renewals/upgrades of a
   * continuing subscription never count. Later non-overlapping events of the same organization supersede
   * earlier ones; truly overlapping ones need a voucher or a single check-in. Works where no voucher is issued.
   */
  promo_new_members: number;
  subscription_vouchers_minted: number;
  total_vouchers_minted: number;
  total_voucher_value_minted: number;
  vouchers_redeemed: number;
  redeemed_voucher_value: number;
  definition_version: number;
  source_max_updated_at: string | null;
  refresh_revision: number;
  refreshed_at: string;
  salespeople: string;
  operators: string;
}

export type EventExecutiveSort =
  | "event_local_date"
  | "event_title"
  | "event_type"
  | "event_status"
  | "invited_people_count"
  | "member_registrations"
  | "promo_new_members"
  | "total_vouchers_minted"
  | "total_voucher_value_minted"
  | "vouchers_redeemed"
  | "redeemed_voucher_value"
  | "refreshed_at";

export interface EventExecutiveSummaryFilter {
  dateFrom?: string;
  dateTo?: string;
  eventType?: string;
  // Default (undefined/true) shows in_progress + completed. false => in_progress only
  // (the "Hide completed" toggle). The base set is always in_progress + completed and is
  // scoped server-side; this only narrows within it.
  includeCompleted?: boolean;
  // Geographic US-state filter. undefined/empty => all states. Codes are matched
  // case-insensitively server-side.
  states?: string[];
  page: number;
  limit: number;
  sortBy?: EventExecutiveSort;
  sortDirection?: "asc" | "desc";
}

export interface EventExecutiveSummaryResult {
  rows: EventExecutiveSummaryRow[];
  total: number;
}

const asFiniteNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizeRow = (row: unknown): EventExecutiveSummaryRow => {
  if (!row || typeof row !== "object") {
    throw new Error("Event Performance returned an invalid row");
  }
  const r = row as Record<string, unknown>;
  if (typeof r.event_id !== "string" || typeof r.event_title !== "string") {
    throw new Error("Event Performance returned an invalid event identity");
  }
  const numericFields = [
    "invited_people_count",
    "campaign_invites_sent_count",
    "reminder_messages_count",
    "total_campaign_messages_count",
    "member_registrations",
    "guest_registrations",
    "member_self_checkins",
    "nonmember_self_checkins",
    "member_operator_checkins",
    "nonmember_operator_checkins",
    "cta_member_checkins",
    "cta_nonmember_checkins",
    "unclassified_member_checkins",
    "unclassified_nonmember_checkins",
    "promo_new_members",
    "subscription_vouchers_minted",
    "total_vouchers_minted",
    "total_voucher_value_minted",
    "vouchers_redeemed",
    "redeemed_voucher_value",
    "definition_version",
    "refresh_revision",
  ] as const;
  const out = { ...r } as unknown as EventExecutiveSummaryRow;
  for (const field of numericFields) {
    out[field] = asFiniteNumber(r[field]);
  }
  out.location_display =
    typeof r.location_display === "string" ? r.location_display : "";
  out.event_state = typeof r.event_state === "string" ? r.event_state : null;
  out.salespeople = typeof r.salespeople === "string" ? r.salespeople : "";
  out.operators = typeof r.operators === "string" ? r.operators : "";
  return out;
};

export async function getEventExecutiveSummaries(
  filter: EventExecutiveSummaryFilter,
): Promise<EventExecutiveSummaryResult> {
  // NOTE: bind to the client — supabase.rpc uses `this` internally (this.rest),
  // so calling a detached reference throws "undefined is not an object (this.rest)".
  const callRpc = supabase.rpc.bind(supabase) as unknown as (
    name: string,
    args: Record<string, unknown>,
  ) => Promise<{
    data: unknown;
    error: { message?: string } | null;
  }>;
  const states =
    filter.states && filter.states.length > 0 ? filter.states : null;
  const { data, error } = await callRpc("list_event_executive_summaries", {
    p_date_from: filter.dateFrom || null,
    p_date_to: filter.dateTo || null,
    p_event_types: filter.eventType ? [filter.eventType] : null,
    p_page: filter.page,
    p_limit: filter.limit,
    p_sort_by: filter.sortBy || "event_local_date",
    p_sort_direction: filter.sortDirection || "desc",
    // Default true keeps completed events visible; the toggle sends false.
    p_include_completed: filter.includeCompleted !== false,
    p_states: states,
  });
  if (error) {
    throw new Error(error.message || "Unable to load Event Performance");
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new Error("Event Performance returned an invalid response");
  }
  const payload = data as { rows?: unknown; total?: unknown };
  if (!Array.isArray(payload.rows)) {
    throw new Error("Event Performance returned an invalid row collection");
  }
  return {
    rows: payload.rows.map(normalizeRow),
    total: asFiniteNumber(payload.total),
  };
}

/**
 * Upcoming section of Event Performance: events whose local start day has NOT yet arrived, within the
 * next `days` days. Server-side scope (migration 2026-10-02-100) is the exact complement of the main
 * table's, so an event moves from here to the main table at local midnight of its start day.
 */
export interface UpcomingEventSummaryRow {
  event_id: string;
  event_title: string;
  event_type: string;
  event_status: string;
  event_start_at: string | null;
  event_end_at: string | null;
  event_timezone: string | null;
  event_local_date: string | null;
  location_display: string;
  event_state: string | null;
  salespeople: string;
  invited_people_count: number;
  campaign_invites_sent_count: number;
  reminder_messages_count: number;
  member_registrations: number;
  guest_registrations: number;
  total_vouchers_minted: number;
  refreshed_at: string | null;
}

export interface UpcomingEventSummaryFilter {
  days: number;
  eventType?: string;
  states?: string[];
}

export interface UpcomingEventSummaryResult {
  rows: UpcomingEventSummaryRow[];
  total: number;
  days: number;
}

const normalizeUpcomingRow = (row: unknown): UpcomingEventSummaryRow => {
  if (!row || typeof row !== "object") {
    throw new Error("Upcoming events returned an invalid row");
  }
  const r = row as Record<string, unknown>;
  if (typeof r.event_id !== "string" || typeof r.event_title !== "string") {
    throw new Error("Upcoming events returned an invalid event identity");
  }
  const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
  return {
    event_id: r.event_id,
    event_title: r.event_title,
    event_type: str(r.event_type) ?? "",
    event_status: str(r.event_status) ?? "",
    event_start_at: str(r.event_start_at),
    event_end_at: str(r.event_end_at),
    event_timezone: str(r.event_timezone),
    event_local_date: str(r.event_local_date),
    location_display: str(r.location_display) ?? "",
    event_state: str(r.event_state),
    salespeople: str(r.salespeople) ?? "",
    invited_people_count: asFiniteNumber(r.invited_people_count),
    campaign_invites_sent_count: asFiniteNumber(r.campaign_invites_sent_count),
    reminder_messages_count: asFiniteNumber(r.reminder_messages_count),
    member_registrations: asFiniteNumber(r.member_registrations),
    guest_registrations: asFiniteNumber(r.guest_registrations),
    total_vouchers_minted: asFiniteNumber(r.total_vouchers_minted),
    refreshed_at: str(r.refreshed_at),
  };
};

export async function getUpcomingEventSummaries(
  filter: UpcomingEventSummaryFilter,
): Promise<UpcomingEventSummaryResult> {
  const callRpc = supabase.rpc.bind(supabase) as unknown as (
    name: string,
    args: Record<string, unknown>,
  ) => Promise<{
    data: unknown;
    error: { message?: string } | null;
  }>;
  const { data, error } = await callRpc(
    "list_upcoming_event_executive_summaries",
    {
      p_days: filter.days,
      p_event_types: filter.eventType ? [filter.eventType] : null,
      p_states:
        filter.states && filter.states.length > 0 ? filter.states : null,
      p_limit: 200,
    },
  );
  if (error) {
    throw new Error(error.message || "Unable to load upcoming events");
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new Error("Upcoming events returned an invalid response");
  }
  const payload = data as { rows?: unknown; total?: unknown; days?: unknown };
  if (!Array.isArray(payload.rows)) {
    throw new Error("Upcoming events returned an invalid row collection");
  }
  return {
    rows: payload.rows.map(normalizeUpcomingRow),
    total: asFiniteNumber(payload.total),
    days: asFiniteNumber(payload.days),
  };
}

/**
 * "Open for registration" panel of Event Performance (migration 2026-10-12-430): upcoming events with
 * a published landing page that still accept registrations, with LIVE (not summary-table) canonical
 * registration counts — the same contract as get_event_attendance_summary / canonicalAttendance — and
 * every cap the public registration path enforces. Polled by the panel; cheap by design.
 */
export interface OpenRegistrationRow {
  event_id: string;
  event_title: string;
  event_type: string;
  event_status: string;
  event_start_at: string | null;
  event_end_at: string | null;
  event_timezone: string | null;
  date_tbd: boolean;
  event_state: string | null;
  location_display: string;
  registration_mode: string | null;
  /** Channels that can still create a NEW registration (invite_gated: invitation only). Null when the
   *  server did not send it; placesLeft() then derives it from registration_mode. */
  accepted_channels: RegistrationChannel[] | null;
  /** Advance-registration RECORDS (Overview funnel "Registered"). */
  registered_rows: number;
  /** People on those records (Overview funnel "N people, in advance"). */
  registered_headcount: number;
  registered_member_headcount: number;
  registered_nonmember_headcount: number;
  registered_rows_24h: number;
  last_registered_at: string | null;
  caps: OpenRegistrationCap[];
}

export interface OpenRegistrationResult {
  rows: OpenRegistrationRow[];
  total: number;
  asOf: string | null;
}

const CAP_KINDS: ReadonlySet<string> = new Set([
  "eib_registration",
  "capacity",
  "pre_registration",
]);

/** Valid channel names only; null for a missing/empty/garbage value (callers then fall back). */
const asChannels = (v: unknown): RegistrationChannel[] | null => {
  if (!Array.isArray(v)) {
    return null;
  }
  const out = v.filter((x): x is RegistrationChannel =>
    (REGISTRATION_CHANNELS as readonly unknown[]).includes(x),
  );
  return out.length > 0 ? out : null;
};

const normalizeOpenRegistrationRow = (row: unknown): OpenRegistrationRow => {
  if (!row || typeof row !== "object") {
    throw new Error("Open for registration returned an invalid row");
  }
  const r = row as Record<string, unknown>;
  if (typeof r.event_id !== "string" || typeof r.event_title !== "string") {
    throw new Error("Open for registration returned an invalid event identity");
  }
  const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
  const caps: OpenRegistrationCap[] = Array.isArray(r.caps)
    ? (r.caps as unknown[]).flatMap((c) => {
        if (!c || typeof c !== "object") {
          return [];
        }
        const cap = c as Record<string, unknown>;
        if (typeof cap.kind !== "string" || !CAP_KINDS.has(cap.kind)) {
          return [];
        }
        // A cap without a usable limit is dropped, never coerced to 0 (which would render "Full").
        // The server only emits caps whose limit IS NOT NULL.
        if (
          !Number.isFinite(Number(cap.limit)) ||
          cap.limit === null ||
          cap.limit === ""
        ) {
          return [];
        }
        return [
          {
            kind: cap.kind as OpenRegistrationCap["kind"],
            limit: asFiniteNumber(cap.limit),
            used: asFiniteNumber(cap.used),
            ...(asChannels(cap.applies_to)
              ? {
                  applies_to: asChannels(
                    cap.applies_to,
                  ) as RegistrationChannel[],
                }
              : {}),
          },
        ];
      })
    : [];
  return {
    event_id: r.event_id,
    event_title: r.event_title,
    event_type: str(r.event_type) ?? "",
    event_status: str(r.event_status) ?? "",
    event_start_at: str(r.event_start_at),
    event_end_at: str(r.event_end_at),
    event_timezone: str(r.event_timezone),
    date_tbd: r.date_tbd === true,
    event_state: str(r.event_state),
    location_display: str(r.location_display) ?? "",
    registration_mode: str(r.registration_mode),
    accepted_channels: asChannels(r.accepted_channels),
    registered_rows: asFiniteNumber(r.registered_rows),
    registered_headcount: asFiniteNumber(r.registered_headcount),
    registered_member_headcount: asFiniteNumber(r.registered_member_headcount),
    registered_nonmember_headcount: asFiniteNumber(
      r.registered_nonmember_headcount,
    ),
    registered_rows_24h: asFiniteNumber(r.registered_rows_24h),
    last_registered_at: str(r.last_registered_at),
    caps,
  };
};

export async function getOpenRegistrationLiveCounts(filter: {
  eventType?: string;
  states?: string[];
}): Promise<OpenRegistrationResult> {
  const callRpc = supabase.rpc.bind(supabase) as unknown as (
    name: string,
    args: Record<string, unknown>,
  ) => Promise<{
    data: unknown;
    error: { message?: string } | null;
  }>;
  const { data, error } = await callRpc("list_open_registration_live_counts", {
    p_event_types: filter.eventType ? [filter.eventType] : null,
    p_states: filter.states && filter.states.length > 0 ? filter.states : null,
    p_limit: 200,
  });
  if (error) {
    throw new Error(error.message || "Unable to load open registrations");
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new Error("Open for registration returned an invalid response");
  }
  const payload = data as { rows?: unknown; total?: unknown; as_of?: unknown };
  if (!Array.isArray(payload.rows)) {
    throw new Error("Open for registration returned an invalid row collection");
  }
  return {
    rows: payload.rows.map(normalizeOpenRegistrationRow),
    total: asFiniteNumber(payload.total),
    asOf: typeof payload.as_of === "string" ? payload.as_of : null,
  };
}
